import { Link } from "react-router-dom";
import { Clock, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { BLOGS } from "@/data/content";

const CATEGORY_COLORS = {
  "Student Stories": "bg-[#FF5A36]/10 text-[#FF5A36]",
  "Mentor Spotlights": "bg-[#FFB323]/15 text-[#B07500]",
  "Industry Insights": "bg-[#0B132B]/8 text-[#0B132B]",
  "Program Updates": "bg-emerald-500/10 text-emerald-700",
};

export default function Blogs() {
  const [featured, ...rest] = BLOGS;

  return (
    <main data-testid="blogs-page">
      <section className="bg-[#0B132B] text-white pt-40 pb-24 grain relative overflow-hidden" data-testid="blogs-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <Reveal>
            <Overline light>Stories & Insights</Overline>
            <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] max-w-4xl">
              The heart of IBY, told one story at a time.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <Reveal>
          <Link to={`/blogs/${featured.slug}`} data-testid="featured-blog-card" className="group grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-24">
            <div className="overflow-hidden rounded-[2rem]">
              <img
                src={featured.img}
                alt={featured.title}
                className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className={`text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full ${CATEGORY_COLORS[featured.category]}`}>
                  {featured.category}
                </span>
                <span className="text-sm text-[#4A5568] flex items-center gap-1"><Clock size={14} /> {featured.readTime}</span>
              </div>
              <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight text-[#0B132B] leading-tight group-hover:text-[#FF5A36] transition-colors duration-300">
                {featured.title}
              </h2>
              <p className="mt-5 text-[#4A5568] leading-relaxed max-w-xl">{featured.excerpt}</p>
              <span className="inline-flex items-center gap-2 mt-6 font-bold text-[#0B132B] group-hover:text-[#FF5A36] group-hover:gap-4 transition-all duration-300">
                Read story <ArrowUpRight size={17} />
              </span>
            </div>
          </Link>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {rest.map((b, i) => (
            <Reveal key={b.slug} delay={i * 0.1}>
              <Link to={`/blogs/${b.slug}`} data-testid={`blog-card-${i}`} className="group block h-full">
                <div className="overflow-hidden rounded-[1.75rem] mb-6">
                  <img src={b.img} alt={b.title} className="w-full aspect-[3/2] object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <span className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${CATEGORY_COLORS[b.category]}`}>
                    {b.category}
                  </span>
                  <span className="text-xs text-[#4A5568] flex items-center gap-1"><Clock size={12} /> {b.readTime}</span>
                </div>
                <h3 className="font-display font-semibold text-xl tracking-tight text-[#0B132B] leading-snug group-hover:text-[#FF5A36] transition-colors duration-300">
                  {b.title}
                </h3>
                <p className="mt-3 text-sm text-[#4A5568] leading-relaxed">{b.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
